/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import type {
    ChangePasswordRequest,
    ConfirmTotpRequest,
    DisableTotpRequest,
    InviteUserRequest,
    ResetPasswordRequest,
    UserListResponse,
    UserRequest,
    UserResponse,
    UserUpdateRequest
} from "../../api/types/swagger-api-types"
import type { ValueOf } from "../../utils/types"

export type UserDetail = UserResponse // used as a response to create/update calls
export type UserList = UserListResponse
export type UserCreate = UserRequest
export type UserUpdate = UserUpdateRequest
export type UserChangePassword = ChangePasswordRequest
export type ResetPassword = ResetPasswordRequest

// User TOTP types
export type DisableTotp = DisableTotpRequest
export type ConfirmTotp = ConfirmTotpRequest
export type TotpCode = Blob

export type UserRoleList = (
    | "ROLE_CONSOLE_API"
    | "ROLE_CONSOLE_API_ADMIN"
    | "ROLE_CONSOLE_API_ORGANIZATION_ADMIN"
    | "ROLE_DEVICE_API"
    | "ROLE_CONSOLE_API_DEVELOPER"
)[]
export type UserListFilter = {
    search: string
    orgId: number | null
    status: boolean | null
    role: UserRole | null
    external: boolean | null
}

export enum UserRole {
    ROLE_CONSOLE_API_ADMIN = "ROLE_CONSOLE_API_ADMIN",
    ROLE_CONSOLE_API_ORGANIZATION_ADMIN = "ROLE_CONSOLE_API_ORGANIZATION_ADMIN",
    ROLE_CONSOLE_API = "ROLE_CONSOLE_API",
    ROLE_DEVICE_API = "ROLE_DEVICE_API",
    ROLE_CONSOLE_API_DEVELOPER = "ROLE_CONSOLE_API_DEVELOPER"
}

export type UserInvite = InviteUserRequest

export const InviteStatus = {
    Success: "success",
    UserNotFound: "user_not_found",
    Error: "error"
} as const

export type InviteStatus = ValueOf<typeof InviteStatus>

export type UserAppTypesState = "not-fetched" | "empty" | "has-app-types"

// Helper function related to UserRole
export function formatRole(role: string): string {
    return role
        .replace("ROLE_CONSOLE_API_", "")
        .replace(/^ROLE_/, "")
        .replace(/_/g, " ")
        .toLowerCase()
        .replaceAll("_", " ")
}

export const toUserRoles = (values: string[]): UserRole[] =>
    values.filter((value): value is UserRole => Object.values(UserRole).includes(value as UserRole))

export const readableUserRoles = (roles: UserRoleList): string[] => {
    const roleMap: Record<UserRole, string> = {
        [UserRole.ROLE_CONSOLE_API_ADMIN]: "Admin",
        [UserRole.ROLE_CONSOLE_API_ORGANIZATION_ADMIN]: "Organization Admin",
        [UserRole.ROLE_CONSOLE_API]: "Console API",
        [UserRole.ROLE_DEVICE_API]: "Device",
        [UserRole.ROLE_CONSOLE_API_DEVELOPER]: "Console Developer"
    }

    return roles.map((role) => roleMap[role] || "Unknown Role")
}
